import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { XIcon, LightningIcon } from "@phosphor-icons/react";
import { Button, radius } from "@libertify/ui";
import cardBg from "@/assets/bkg-modal-buyflow.png";
import { upgradeCardCookie } from "@/utils/upgradeCardCookie";

export type UpgradeCardProps = {
  onDismiss?: () => void;
};

export const UpgradeCard = ({ onDismiss }: UpgradeCardProps) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  const handleDismiss = () => {
    setClosing(true);
    setTimeout(() => {
      setHidden(true);
      upgradeCardCookie.setDismissed();
      onDismiss?.();
    }, 300);
  };

  const handleUpgrade = () => {
    navigate("/subscription/subscriptions");
  };

  return (
    <StyledCardWrapper $closing={closing}>
      <StyledArrow />
      <StyledCard>
        <StyledCloseButton
          onClick={handleDismiss}
          aria-label={t("buttons.close")}
          type="button"
        >
          <XIcon size={24} weight="bold" />
        </StyledCloseButton>

        <StyledBadge>{t("quota.levelUp")}</StyledBadge>
        <StyledTextContent>
          <StyledTitle>{t("quota.outOfCredits")}</StyledTitle>
          <StyledDescription>
            {t("quota.outOfCreditsDescription")}
          </StyledDescription>
        </StyledTextContent>

        <StyledUpgradeButton as={Button} onClick={handleUpgrade}>
          <LightningIcon size={24} />
          {t("quota.upgradeToPro")}
        </StyledUpgradeButton>
      </StyledCard>
    </StyledCardWrapper>
  );
};

const StyledCardWrapper = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 433px;
  height: 295px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  opacity: ${({ $closing }) => ($closing ? 0 : 1)};
  transform: ${({ $closing }) =>
    $closing ? "translateY(-8px)" : "translateY(0)"};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  @media (width <= 768px) {
    width: 100%;
    height: auto;
  }
`;

const StyledArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #6946d5;
  margin-right: 40px;
`;

const StyledCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-height: 210px;
  padding: 24px;
  border-radius: ${radius.box}px;
  background: url(${cardBg}) no-repeat center / cover;
  color: #fff;
  overflow: hidden;
`;

const StyledCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${radius.circle};
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const StyledBadge = styled.span`
  min-width: 92px;
  min-height: 23px;
  align-items: center;
  justify-content: center;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.48px;
  display: inline-flex;
  padding: 4px 12px;
  border-radius: ${radius.pill}px;
  background: #090e19;
  text-transform: uppercase;
  white-space: nowrap;
  color: #fff;
`;

const StyledTextContent = styled.div`
  width: 70%;
`;

const StyledTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-family: "Poppins", sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 118%;
  letter-spacing: -1.12px;
`;

const StyledDescription = styled.p`
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 135%;
  letter-spacing: -0.3px;
`;

const StyledUpgradeButton = styled.button`
  margin-top: -8px;
  border-radius: ${radius.pill}px;
  border: 3px solid #3bd4ca;
  background: #fff;
  box-shadow: 0 3px 30px 0 rgba(0, 0, 0, 0.15);
  max-height: 40px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  display: inline-flex;
  align-self: flex-end;
  padding: 12px 24px 12px 18px;
  color: #6946d5;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.15px;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition:
    background 0.15s,
    box-shadow 0.15s;

  &:hover {
    background: #f3f0ff;
    box-shadow: 0 2px 8px rgba(105, 70, 213, 0.25);
  }
`;
